import axios from "axios";
import { authService } from "./authService";
import store from "../app/store";
import { startLoading, stopLoading } from "../features/ui/uiSlice";
import json from "../secret.json";

const API_BASE_URL = json.API_BASE_URL;

const instance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Refresh 요청용 인스턴스 (Interceptor 돌기 전 순수 인스턴스)
const refreshInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// 진행 중인 토큰 갱신. 여러 요청이 동시에 401 을 받아도 갱신은 한 번만 나간다.
//
// 리프레시 토큰은 한 번 쓰면 폐기된다(서버의 SIMPLE_JWT 가 ROTATE_REFRESH_TOKENS +
// BLACKLIST_AFTER_ROTATION). 그래서 요청마다 각자 갱신을 부르면 첫 하나만 성공하고
// 나머지는 401 을 받는다. 아래 로직이 그것을 '로그인 만료' 로 보고 토큰을 지우며
// 로그인 화면으로 보내므로, 진행 중이던 작업이 통째로 사라진다. 학생 여러 명을
// 한꺼번에 지웠을 때 한 명도 안 지워진 것이 이 때문이었다.
let refreshPromise = null;

function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = authService.getRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error("리프레시 토큰이 없습니다."));
  }

  refreshPromise = refreshInstance
    .post("/api/v1/token/refresh", { refresh: refreshToken })
    .then((response) => {
      const { access, refresh } = response.data;
      authService.setTokens(access, refresh);
      return access;
    })
    .finally(() => {
      // 다음 만료 때는 새 리프레시 토큰으로 다시 한 번 갱신할 수 있어야 한다.
      refreshPromise = null;
    });

  return refreshPromise;
}

// 로그인 만료 안내가 요청 수만큼 뜨지 않게 한 번만 처리한다.
let isSessionExpiredHandled = false;

function handleSessionExpired() {
  if (isSessionExpiredHandled) {
    return;
  }
  isSessionExpiredHandled = true;

  authService.removeTokens();
  alert("로그인이 만료되었습니다. 다시 로그인해주세요.");
  window.location.href = "/login";
}

// 요청 인터셉터
instance.interceptors.request.use(
  (config) => {
    // withAuth 옵션이 true인 경우에만 Authorization 헤더에 토큰 주입
    if (config.withAuth) {
      const token = authService.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    store.dispatch(startLoading());
    return config;
  },
  (error) => {
    store.dispatch(stopLoading());

    return Promise.reject(error);
  }
);

// 응답 인터셉터
instance.interceptors.response.use(
  (response) => {
    store.dispatch(stopLoading());
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 401 발생 시 Refresh Token 로직
    if (
      error.response &&
      error.response.status === 401 &&
      originalRequest.withAuth &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const access = await refreshAccessToken();

        originalRequest.headers["Authorization"] = `Bearer ${access}`;
        const finalResponse = await instance(originalRequest);
        store.dispatch(stopLoading());

        return finalResponse;
      } catch (refreshError) {
        store.dispatch(stopLoading());
        handleSessionExpired();

        return Promise.reject(refreshError);
      }
    }
    store.dispatch(stopLoading());
    return Promise.reject(error);
  }
);

const apiClient = {
  async get(endpoint, config = {}) {
    const response = await instance.get(endpoint, config);
    return response;
  },

  async post(endpoint, data, config = {}) {
    const response = await instance.post(endpoint, data, config);
    return response;
  },

  async put(endpoint, data, config = {}) {
    const response = await instance.put(endpoint, data, config);
    return response;
  },

  async delete(endpoint, config = {}) {
    const response = await instance.delete(endpoint, config);
    return response;
  },

  // put, delete 등 필요 시 추가
};

export default apiClient;
